/**
 * <TimeSlotGrid> — la grille hebdomadaire jours × créneaux (organism).
 *
 * Le parent coche TOUTES ses disponibilités : plus il y en a, plus le routage automatique a de
 * chances de trouver un cours. La grille ne possède donc rien — ni la sélection (`selectedIds` +
 * `onToggle`), ni la liste des jours : l'écran ne passe que les jours atteignables.
 *
 * **Deux dispositions, une seule cellule.** Au large, un jour = une colonne, avec ses créneaux
 * dessous ; à l'étroit, la grille se replie en journées empilées et les créneaux passent à la
 * ligne. C'est la largeur DU CONTENEUR qui tranche (`onLayout`), jamais celle de la fenêtre :
 * la même grille vit dans une feuille de 500 px et dans une page de 1120. Même mécanisme que
 * `<SessionRow>` et `<NextSessionHero>` — on ne réinvente pas une seconde façon de mesurer.
 *
 * Un créneau est une **pilule de sélection** (`<ChoiceChip variant="pill">`), comme les minutes de
 * retard : la forme du poussoir, mais un état, pas une action. Coché, il passe à l'aplat de
 * l'univers — le vert du legacy est la couleur de la progression dans le livre, et un créneau
 * choisi n'est pas un progrès.
 * Aucune copie ici : les mots de l'étape appartiennent au geste qui la montre.
 */
import React, { useState } from "react"
import { View, type LayoutChangeEvent } from "react-native"
import { StyleSheet } from "react-native-unistyles"

import { pushButtonChromeWidth } from "../../atoms/Buttons/Button/Button"
import { ChoiceChip } from "../../atoms/ChoiceChip"
import { Row } from "../../atoms/containers/Row"
import { Stack } from "../../atoms/containers/Stack"
import { SectionLabel } from "../../atoms/typography/SectionLabel"
import { Text } from "../../atoms/typography/Text"
import { type UITimeSlot, type UITimeSlotDay } from "../../../ports/primary/ui/types"
import { fontSizes } from "../../theme/tokens/typography"

/**
 * Largeur minimale d'une colonne : le plus large des libellés, « 08h-09h » en `text-sm` gras, mesure 4,15 em en
 * Outfit, espacement compris ; la pilule (une face de poussoir `sm`) y ajoute ses retraits, son contour et le glyphe
 * qu'un créneau peut porter (la coche d'un créneau choisi, la caméra d'un créneau qui a un live), plus 2 px de marge.
 * Mesuré sur une pilule de 95 px (03/11) : à 12 px, « 18h-19h » à côté de sa coche tenait à 0 px près ; à 13 px, il
 * passait à la ligne. Lue sur le jeton et sur le poussoir, la largeur suit l'échelle. En dessous, la grille se replie
 * plutôt que de couper ses libellés.
 */
const LABEL_EM = 4.15
const MIN_COLUMN_WIDTH = Math.ceil(fontSizes["text-sm"] * LABEL_EM) + pushButtonChromeWidth("sm", { glyph: true }) + 2
/** L'écart entre deux colonnes — le jeton `spacing["2"]`, répété ici pour calculer le seuil. */
const COLUMN_GAP = 8

/** Un jour de la grille — ses créneaux peuvent porter plus qu'un id et un libellé (`renderSlot`). */
export type TimeSlotGridDay<S extends UITimeSlot = UITimeSlot> = Omit<UITimeSlotDay, "slots"> & { slots: S[] }

type TimeSlotGridBaseProps<S extends UITimeSlot> = {
    /** Les jours proposés, déjà filtrés par l'écran (échéance, dimanche…). */
    days: TimeSlotGridDay<S>[]
    /** Ce qu'on dit quand aucun jour n'est proposé. */
    emptyMessage?: string
    /** Une ligne courte sous le nom du jour — « 2/5 dispos · 1 live ». Absente (défaut) : rien. */
    dayCaption?: (day: TimeSlotGridDay<S>) => string | undefined
    testID?: string
}

/**
 * Deux façons de remplir la grille :
 *  - **la sélection** (`selectedIds` + `onToggle`) — chaque créneau est la pilule de sélection,
 *    cochée ou non : la grille de la famille ;
 *  - **la cellule** (`renderSlot`) — quand cocher ne suffit pas à dire ce qu'est un créneau : la
 *    semaine du tuteur a quatre états (`<TutorWeekGrid>`). `fullWidth` dit la disposition en
 *    colonnes, où la cellule remplit la sienne.
 */
export type TimeSlotGridProps<S extends UITimeSlot = UITimeSlot> = TimeSlotGridBaseProps<S> &
    (
        | { selectedIds: string[]; onToggle: (slotId: string) => void; renderSlot?: never }
        | { renderSlot: (slot: S, fullWidth: boolean) => React.ReactNode; selectedIds?: never; onToggle?: never }
    )

export function TimeSlotGrid<S extends UITimeSlot = UITimeSlot>(props: TimeSlotGridProps<S>) {
    const { days, emptyMessage, dayCaption, testID } = props

    // `null` tant que rien n'est mesuré : on démarre replié, jamais en colonnes — une grille qui
    // se replie au premier rendu se verrait, des colonnes qui se forment ne se voient pas.
    const [width, setWidth] = useState<number | null>(null)
    const measure = (event: LayoutChangeEvent) => setWidth(event.nativeEvent.layout.width)

    const needed = days.length * MIN_COLUMN_WIDTH + Math.max(0, days.length - 1) * COLUMN_GAP
    const asColumns = width != null && days.length > 0 && width >= needed

    const pill = (slot: S) =>
        props.renderSlot != null ? (
            <React.Fragment key={slot.id}>{props.renderSlot(slot, asColumns)}</React.Fragment>
        ) : (
            <ChoiceChip
                key={slot.id}
                variant="pill"
                label={slot.label}
                selected={props.selectedIds.includes(slot.id)}
                onPress={() => props.onToggle(slot.id)}
                fullWidth={asColumns}
            />
        )

    const dayHead = (day: TimeSlotGridDay<S>) => {
        const caption = dayCaption?.(day)
        if (caption == null || caption === "") return <SectionLabel>{day.label}</SectionLabel>
        return (
            <Stack gap="0">
                <SectionLabel>{day.label}</SectionLabel>
                <Text size="text-sm" color="text-muted">
                    {caption}
                </Text>
            </Stack>
        )
    }

    if (days.length === 0) {
        return (
            <View onLayout={measure} testID={testID}>
                {emptyMessage != null && emptyMessage !== "" && (
                    <Text size="text-md" color="text-muted">
                        {emptyMessage}
                    </Text>
                )}
            </View>
        )
    }

    return (
        <View onLayout={measure} testID={testID}>
            {asColumns ? (
                <Row gap="2" align="start">
                    {days.map((day) => (
                        <Stack key={day.id} gap="2" style={styles.column}>
                            {dayHead(day)}
                            {day.slots.map(pill)}
                        </Stack>
                    ))}
                </Row>
            ) : (
                <Stack gap="4">
                    {days.map((day) => (
                        <Stack key={day.id} gap="2">
                            {dayHead(day)}
                            <View style={styles.wrap}>{day.slots.map(pill)}</View>
                        </Stack>
                    ))}
                </Stack>
            )}
        </View>
    )
}

const styles = StyleSheet.create((theme) => ({
    /** Les colonnes se partagent la largeur à parts égales, sans jamais passer sous le seuil. */
    column: { flex: 1, minWidth: MIN_COLUMN_WIDTH },
    wrap: {
        flexDirection: "row",
        flexWrap: "wrap",
        gap: theme.spacing["2"],
    },
}))
